import { React, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { countDocumentMatchRows } from './components/documentMatchRows';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ReconcileSideBar from './reconcileSideBar';
import ReconcileDocumentSideBar from './reconcileDocumentSideBar';
import ReconcileComments from './reconcileComments';
import ReconcileContabilizar from './contabilidad/reconcileContabilizar';
import ReconcileAttachments from './reconcileAttachments';
import { useDispatch, useSelector } from "react-redux";
import ReconciledTable from './reconciledTable';
import { FindDocumentById, GetDocumentById } from '../../../../../graphql/documents';
import { GetTransaction, FindMatchsByTransactionId } from '../../../../../graphql/conciliacion';
import { importUpdateTransactions } from '../../../../../redux/actions/conciliacion';
import { importOpenSidebar } from 'src/redux/actions/openSidebar';
import { setUpdateDocuments } from 'src/redux/actions/documentostributarios';
import { showAccounting } from 'src/utils/accounting';
import FilterTabs from '../../../../FilterTabs/FilterTabs';
import { dt, getReconcileSidebarTabs } from 'src/utils/documentsI18n';
import {
  countDocumentReconcileEntries,
  countTransactionReconcileEntries,
} from 'src/utils/reconcileEntriesCount';

const Reconcile = ({setCheckedTransactions}) => {

  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { roles } = useSelector((state) => state.auth);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const companyId = selectedCompany?.id;
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const { updateTransactions } = useSelector((state) => state.conciliacion);
  const { updateDocuments } = useSelector((state) => state.documentostributarios);
  const urlSearchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const transactionIdFromUrl =
    urlSearchParams.get('transactionId') ||
    (urlSearchParams.get('isComment') === 'true' ? urlSearchParams.get('id') : null);
  const documentIdFromUrl = urlSearchParams.get('documentId');

  // Prefer explicit sidebar context to prevent type "flipping" across renders.
  const editDataType = useMemo(() => {
    const sidebarType = openSidebar?.options?.type;
    if (sidebarType === 'transactions') return 'transaction';
    if (sidebarType === 'documents') return 'document';
    if (transactionIdFromUrl || location.pathname.includes('conciliacion')) {
      return 'transaction';
    }
    if (documentIdFromUrl || location.pathname.includes('documentos_tributarios')) {
      return 'document';
    }
    const ed = openSidebar?.editData;
    if (ed?.meta?.documentType || ed?.documentType) return 'document';
    if (ed?.type === 'SPEND' || ed?.type === 'RECIEVED' || ed?.bankId) {
      return 'transaction';
    }
    return 'transaction';
  }, [
    openSidebar?.options?.type,
    openSidebar?.editData,
    transactionIdFromUrl,
    documentIdFromUrl,
    location.pathname,
  ]);

  const [data, setData] = useState();
  const [notesCount, setNotesCount] = useState(0);
  const [entriesCount, setEntriesCount] = useState(0);
  const [matchesCount, setMatchesCount] = useState(0);
  const [activeTab, setActiveTab] = useState('match');

  // Redirect away from contabilizar tab if accounting is disabled or company lacks contabilidad plan
  useEffect(() => {
    if (activeTab === 'contabilizar' && !showAccounting(roles, selectedCompany)) {
      setActiveTab('match');
    }
  }, [activeTab, roles, selectedCompany]);

  // NOTE: TransactionTable handles URL-based fetching and Redux updates
  // This component only reads from Redux to avoid duplicate API calls

  const currentId = useMemo(() => {
    if (editDataType === 'document') {
      return openSidebar?.editData?._id || openSidebar?.editData?.id || null;
    }
    return openSidebar?.editData?.id || openSidebar?.editData?._id || null;
  }, [editDataType, openSidebar?.editData?._id, openSidebar?.editData?.id]);

  const documentIds = useMemo(() => {
    return editDataType === 'document' && currentId ? [currentId] : [];
  }, [editDataType, currentId]);

  const findDocumentVariables = useMemo(() => {
    return { ids: documentIds.length > 0 ? [...documentIds] : [] };
  }, [documentIds.join(',')]);

  const findDocumentById = FindDocumentById(findDocumentVariables);
  
  // Fetch ALL match types (documents, transactions, manual, clay) for accurate count
  const { data: serverMatchData } = FindMatchsByTransactionId({
    transactionId: currentId,
    dte: editDataType === 'document',
  });

  // Compute total match count from server data (all match types)
  const serverMatchesCount = useMemo(() => {
    if (!serverMatchData) return null; // null = not loaded yet
    return (serverMatchData.allMatchsByTransactionId?.length || 0) +
      (serverMatchData.allMatchManualByTransactionId?.length || 0) +
      (serverMatchData.allMatchByClay?.length || 0) +
      (serverMatchData.allTransactionsByDocumentId?.length || 0);
  }, [serverMatchData]);

  const resolveEntriesCount = useCallback(
    (payload, type = editDataType) => {
      if (!payload) return 0;
      if (type === 'document') {
        return countDocumentReconcileEntries(payload);
      }
      return countTransactionReconcileEntries(payload);
    },
    [editDataType],
  );

  // Set up transaction hook but don't fetch initially (TransactionTable handles URL-based fetching)
  // We only fetch if explicitly needed (e.g., when refetching for updates)
  // The hook is set up so reexecuteQuery works when needed
  const getTransaction = GetTransaction(null);

  const urlActiveIndex = useMemo(() => {
    if (urlSearchParams.get('isComment') === 'true') return 2;
    if (urlSearchParams.get('isAccounting') === 'true') return 1;
    if (urlSearchParams.get('isFiles') === 'true') return 3;
    return null;
  }, [urlSearchParams]);

  let activeIndex = openSidebar?.options?.activeIndex ?? urlActiveIndex;
  const showOnlyActiveIndexTab = openSidebar?.options?.showOnlyActiveIndexTab === true;

  // Prevent infinite loops / stale UI:
  // - We still avoid dispatching blindly, but we MUST allow refetches for the same id to update
  //   (e.g. after unmatch, candidates change).
  // - Track a signature of the last applied payload instead of only the id.
  const lastProcessedSignatureRef = useRef(null);
  const lastSidebarSyncSignatureRef = useRef(null);
  useEffect(() => {
    // Reset local state when switching items/types
    if (currentId) {
      // Reset signatures when switching items/types so the next payload always applies.
      lastProcessedSignatureRef.current = null;
      lastSidebarSyncSignatureRef.current = null;
      const editDataId = openSidebar?.editData?.id || openSidebar?.editData?._id;
      const editDataMatchesCurrentId = editDataId != null && String(editDataId) === String(currentId);
      if (editDataMatchesCurrentId && openSidebar?.editData) {
        // Already have editData for this id (e.g. opened from "Ver") - show it immediately to avoid visible reload
        const ed = openSidebar.editData;
        setData(ed);
        if (editDataType === 'document') {
          setNotesCount(ed?.meta?.innerComments?.length || 0);
          setEntriesCount(resolveEntriesCount(ed, 'document'));
          setMatchesCount(countDocumentMatchRows(ed?.meta?.matchs));
        } else {
          setNotesCount(ed?.innerComments?.length || 0);
          setEntriesCount(resolveEntriesCount(ed, 'transaction'));
          setMatchesCount(ed?.matchs?.length || 0);
        }
      } else {
        setData(undefined);
        setNotesCount(0);
        setEntriesCount(0);
        setMatchesCount(0);
      }
    }
  }, [currentId, editDataType, openSidebar?.editData, resolveEntriesCount]);

  useEffect(() => {
    if (editDataType === 'document') {
      // Quick render from Redux while query resolves
      if (openSidebar?.editData && (!data || (data?._id || data?.id) !== currentId)) {
        setData(openSidebar.editData);
        setNotesCount(openSidebar.editData?.meta?.innerComments?.length || 0);
        setEntriesCount(resolveEntriesCount(openSidebar.editData, 'document'));
        setMatchesCount(countDocumentMatchRows(openSidebar.editData?.meta?.matchs));
      }

      if (findDocumentById.fetching || !findDocumentById.data) return;
      const [resp] = findDocumentById?.data?.findDocumentsById || [];
      if (!resp) return;

      const respId = resp?._id || resp?.id;
      if (!respId) return;

      // Skip if already applied
      const respSignature = [
        'doc',
        String(respId),
        String(resp?.updatedAt || ''),
        String(resp?.meta?.remaining ?? ''),
        String(resp?.meta?.matchs?.inMatch?.length || 0),
        String(resp?.meta?.matchs?.outMatch?.length || 0),
        String(resp?.meta?.innerComments?.length || 0),
        String(resp?.entries?.entryList?.length || 0),
      ].join('|');

      if (lastProcessedSignatureRef.current === respSignature) return;
      lastProcessedSignatureRef.current = respSignature;

      setNotesCount(resp?.meta?.innerComments?.length || 0);
      setEntriesCount(resolveEntriesCount(resp, 'document'));
      setMatchesCount(countDocumentMatchRows(resp?.meta?.matchs));
      setData(resp);
      return;
    }

    // transaction
    const fetchedTransaction = getTransaction.data?.getTransaction;
    const reduxTransaction = openSidebar?.editData;
    const transactionData = fetchedTransaction || reduxTransaction;
    if (!transactionData) return;

    const txId = transactionData?.id || transactionData?._id;
    if (!txId) return;

    const finalTransactionData = fetchedTransaction
      ? {
          ...fetchedTransaction,
          strongMatchCandidates:
            fetchedTransaction.strongMatchCandidates ||
            reduxTransaction?.strongMatchCandidates,
          weakMatchCandidates:
            fetchedTransaction.weakMatchCandidates ||
            reduxTransaction?.weakMatchCandidates,
        }
      : transactionData;

    // Build a signature so refetches for the same txId can still update state.
    const txSignature = [
      String(txId),
      String(finalTransactionData?.updatedAt || ''),
      String(finalTransactionData?.remaining ?? ''),
      String(finalTransactionData?.matchs?.length || 0),
      String(finalTransactionData?.strongMatchCandidates?.length || 0),
      String(finalTransactionData?.weakMatchCandidates?.length || 0),
      String(finalTransactionData?.innerComments?.length || 0),
      String(finalTransactionData?.entries?.entryList?.length || 0),
    ].join('|');

    if (lastProcessedSignatureRef.current === txSignature) return;
    lastProcessedSignatureRef.current = txSignature;

    setNotesCount(finalTransactionData?.innerComments?.length || 0);
    setEntriesCount(resolveEntriesCount(finalTransactionData, 'transaction'));
    setMatchesCount(finalTransactionData?.matchs?.length || 0);
    setData(finalTransactionData);

    // IMPORTANT:
    // ReconcileSideBar lee remaining/matchs desde `openSidebar.editData`.
    // Tras un cierre completo (p. ej. traspaso entre cuentas) no hay candidatos;
    // igual hay que sincronizar editData o el detalle queda en "sin conciliar"
    // mientras la lista ya muestra Conciliado.
    if (fetchedTransaction && openSidebar?.options?.type !== 'documents') {
      const sidebarSig = `sidebar:${txSignature}`;
      if (lastSidebarSyncSignatureRef.current !== sidebarSig) {
        lastSidebarSyncSignatureRef.current = sidebarSig;
        dispatch(
          importOpenSidebar({
            ...openSidebar,
            editData: finalTransactionData,
          })
        );
      }
    }
  }, [editDataType, currentId, findDocumentById.data, findDocumentById.fetching, getTransaction.data]);

  // Update notesCount and entriesCount when data changes (e.g., after adding a comment or entry)
  useEffect(() => {
    if (editDataType === 'document') {
      if (data?.meta?.innerComments) {
        setNotesCount(data.meta.innerComments.length);
      }
    } else {
      if (data?.innerComments) {
        setNotesCount(data.innerComments.length);
      }
    }
    if (data) {
      setEntriesCount(resolveEntriesCount(data, editDataType));
    }
    if (editDataType === 'document') {
      setMatchesCount(countDocumentMatchRows(data?.meta?.matchs));
    } else if (editDataType === 'transaction') {
      setMatchesCount(data?.matchs?.length ?? 0);
    }
  }, [data, editDataType, resolveEntriesCount]);

  const refetchData = useCallback(() => {
    // Allow same-id refetches to apply by clearing the last applied signature.
    lastProcessedSignatureRef.current = null;
    if (editDataType === 'document' && documentIds.length > 0) {
      findDocumentById.reexecuteQuery({
        requestPolicy: 'network-only',
        variables: { ids: documentIds },
      });
    }
    if (editDataType === 'transaction' && currentId) {
      getTransaction.reexecuteQuery({
        requestPolicy: 'network-only',
        variables: { id: currentId },
      });
    }
  }, [editDataType, currentId, documentIds.join(','), findDocumentById.reexecuteQuery, getTransaction.reexecuteQuery]);

  // Ensure we have candidates before allowing reconciliation logic that depends on them.
  // Some entry points open the sidebar with a lightweight transaction object that may not
  // include `strongMatchCandidates/weakMatchCandidates`.
  useEffect(() => {
    if (editDataType !== 'transaction' || !currentId) return;
    const tx = openSidebar?.editData;
    const hasCandidatesFields =
      tx &&
      (Array.isArray(tx?.strongMatchCandidates) || Array.isArray(tx?.weakMatchCandidates));

    if (!hasCandidatesFields) {
      getTransaction.reexecuteQuery({
        requestPolicy: 'network-only',
        variables: { id: currentId },
      });
    }
  }, [editDataType, currentId, openSidebar?.editData, getTransaction.reexecuteQuery]);

  // Listen for transaction/document updates and refetch (single source of truth)
  useEffect(() => {
    if (updateTransactions === true) {
      refetchData();
      dispatch(importUpdateTransactions(false));
    }
  }, [updateTransactions, refetchData, dispatch]);

  useEffect(() => {
    if (updateDocuments?.value === true) {
      refetchData();
      dispatch(setUpdateDocuments({ value: false }));
    }
  }, [updateDocuments?.value, refetchData, dispatch]);

  // Set initial active tab based on activeIndex from openSidebar
  // Default to 'match' (Matchs) when component loads
  useEffect(() => {
    if (activeIndex === 0) setActiveTab('match');
    else if (activeIndex === 1) setActiveTab('contabilizar');
    else if (activeIndex === 2) setActiveTab('notes');
    else if (activeIndex === 3) setActiveTab('files');
    else if (activeIndex === undefined || activeIndex === null) {
      // Default to 'match' (Matchs) if no activeIndex is set
      setActiveTab('match');
    }
  }, [activeIndex]);

  // Ensure default tab is 'match' on initial mount
  useEffect(() => {
    if (!openSidebar?.editData && activeTab !== 'match') {
      setActiveTab('match');
    }
  }, [openSidebar?.editData]);

  // Preferir conteo en editData del sidebar (actualizado al instante tras unmatch); servidor puede ir desfasado
  const liveMatchCountFromSidebar = useMemo(() => {
    const ed = openSidebar?.editData;
    if (!ed) return null;
    if (editDataType === 'document') {
      return countDocumentMatchRows(ed.meta?.matchs);
    }
    if (editDataType === 'transaction' && Array.isArray(ed.matchs)) {
      return ed.matchs.length;
    }
    return null;
  }, [openSidebar?.editData, editDataType]);

  const displayMatchesCount =
    liveMatchCountFromSidebar !== null
      ? liveMatchCountFromSidebar
      : serverMatchesCount !== null
        ? serverMatchesCount
        : matchesCount;

  const filesCount = useMemo(() => {
    const ed = data || openSidebar?.editData;
    return ed?.attachedFiles?.length || 0;
  }, [data, openSidebar?.editData]);

  const tabs = useMemo(
    () =>
      getReconcileSidebarTabs({
        matches: displayMatchesCount,
        entries: entriesCount,
        comments: notesCount,
        files: filesCount,
      }),
    [displayMatchesCount, entriesCount, notesCount, filesCount]
  );

  const filteredTabs = useMemo(() => {
    const accountingFilteredTabs = tabs.filter(
      (tab) => tab.value !== 'contabilizar' || showAccounting(roles, selectedCompany)
    );
    return showOnlyActiveIndexTab
      ? [accountingFilteredTabs[activeIndex]]
      : accountingFilteredTabs;
  }, [
    showOnlyActiveIndexTab,
    activeIndex,
    tabs,
    roles,
    selectedCompany,
    i18n.language,
  ]);

  const handleTabChange = (tabValue) => {
    if (tabValue === activeTab) return;
    setActiveTab(tabValue);
    // Refetch when switching to match tab
    if (tabValue === 'match') {
      refetchData();
    }
    // Note: ReconcileContabilizar handles its own data fetching via GetEntriesBySource
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'match':
        // Use ReconcileDocumentSideBar for documents, ReconcileSideBar for transactions
        return editDataType === 'document'
          ? <ReconcileDocumentSideBar documentData={data || openSidebar?.editData} />
          : <ReconcileSideBar />;
      case 'contabilizar':
        if (!showAccounting(roles, selectedCompany)) {
          return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p style={{ fontSize: '16px', color: '#666' }}>
                {dt('reconcile.accountingUnavailable')}
              </p>
            </div>
          );
        }
        return <ReconcileContabilizar 
          key={activeTab} // Force remount when tab changes to trigger refresh
          documentData={editDataType === 'document' ? (data || openSidebar?.editData) : null}
          onEntryCreated={() => {
            setActiveTab('contabilizar');
            refetchData();
          }}
          onRefetchSource={refetchData}
        />;
      case 'notes':
        return <ReconcileComments 
          findDocumentById={findDocumentById} 
          getTransaction={getTransaction} 
          data={data} 
          dataType={editDataType} 
          companyId={companyId}
          infoBarEditData={data || openSidebar?.editData}
        />;
      case 'files':
        return <ReconcileAttachments
          data={data || openSidebar?.editData}
          dataType={editDataType}
          findDocumentById={findDocumentById}
          getTransaction={getTransaction}
          companyId={companyId}
        />;
      default:
        return null;
    }
  }

  return (
    <div style={{ overflowX: 'hidden', overflowY: 'visible', width: '100%' }}>
      {
        (data || openSidebar?.editData || openSidebar?.editData?.matchMultiple) &&
        <>
          <FilterTabs
            tabs={filteredTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          <div style={{ marginTop: '20px', overflowX: 'hidden', overflowY: 'visible', width: '100%' }}>
            {renderContent()}
          </div>
        </>
      }
    </div>
  );
};

export default Reconcile;
